import type { ReactElement, Ref } from 'react';
import { forwardRef } from 'react';
import { deprecate } from '../../utils/deprecate';
import { DivButton } from './DivButton';
import type { DivButtonProps } from './DivButton';
import { LinkButton } from './LinkButton';
import type { LinkButtonProps } from './LinkButton';
import { RealButton } from './RealButton';
import type { RealButtonProps } from './RealButton';

export { DivButton, type DivButtonProps } from './DivButton';
export { LinkButton, type LinkButtonProps } from './LinkButton';
export { RealButton, type RealButtonProps } from './RealButton';

export type ButtonProps = LinkButtonProps | RealButtonProps | DivButtonProps;

export type ButtonPropsWith<OtherProps> =
  | (DivButtonProps & OtherProps)
  | (RealButtonProps & OtherProps)
  | (LinkButtonProps & OtherProps);

/**
 * Render a link, a div or a button with the same styling
 * @deprecated use Button from kitt-universal instead
 */
export const Button = forwardRef(
  (props: ButtonProps, ref: Ref<HTMLAnchorElement | HTMLLinkElement | HTMLDivElement>): ReactElement => {
    if (
      __DEV__ &&
      props.variant === 'ghost' &&
      (props.type === undefined || (props.type !== undefined && !['primary', 'secondary'].includes(props.type)))
    ) {
      throw new Error('variant=ghost is only allowed with type=primary or type=secondary');
    }

    if (__DEV__) {
      // @ts-expect-error danger is deprecated
      if (props.type === 'danger') {
        deprecate('Button', 'type="danger" is deprecated.');
      }
    }

    if (props.href != null || props.to !== undefined) {
      return <LinkButton ref={ref as unknown as Ref<HTMLAnchorElement>} {...(props as LinkButtonProps)} />;
    }

    if (__DEV__) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((props as any).type === 'link') {
        throw new Error('type=link is only allowed with href or to prop');
      }
    }

    if (props.submit || props.reset) {
      return <RealButton ref={ref as unknown as Ref<HTMLButtonElement>} {...(props as RealButtonProps)} />;
    }

    return <DivButton ref={ref as unknown as Ref<HTMLDivElement>} {...(props as DivButtonProps)} />;
  },
);
